import React, { useRef } from 'react';
import { HiddenFormField, SelectFormField, LengthFormField, StringFormField, NumberFormField, DelayedNumberFormField, DelayedNumberWithUnitFormField, CurrencyFormField, CurrencyExchange, PriceFormField, UserUnitPrice, UserUnitPriceAttributeField, DescriptionLong, LayoutPartGroupsWindows, LayoutPartGroupsWindowsHidden, TextAreaFormField, FileFormField, CheckboxesFormField, BooleanFormField, ReadOnlyHashFormField} from 'components/layout_and_quote_builder/quote_builder/form_fields';
import DrawingFiltering from 'components/system_commands/form_fields/DrawingFiltering.jsx';
import LayoutDimensionSelectFormField from 'components/quote_builder/form_fields/LayoutDimensionSelectFormField';
// One implementation serving both trees, the same way LayoutDimensionSelectFormField above does --
// two copies of a form field drift, and this one renders in the legacy tree today.
import DelayedNumberWithStaticUnitsFormField from 'components/quote_builder/form_fields/DelayedNumberWithStaticUnitsFormField';
import { guillotineCutCountDefault } from 'components/layout_and_quote_builder/quote_builder/guillotineCutCount';
import hash from "object-hash";

// Expose the guillotine cut-count formula globally so the sections.yml expressions
// (default_value_eval / listen_conditions value_eval, evaluated by bare name) can call it. Using
// window keeps the name stable through minification, the same way user.js exposes withinPercent.
if (typeof window !== 'undefined') {
  window.guillotineCutCountDefault = guillotineCutCountDefault;
}

const FormFieldFactory = ({
  config,
  libraryTabConfig,
  index,
  values,
  options,
  setValue,
  readOnlyValues,
  setFetchInProgress,
  setChildSubFormOpen,
  disabled,
  readOnly,
  register,
  errors,
  context,
  item,
  navSection,
  requestPublishItemOnChange,
  requestGetExtraItems,
  navSectionColumnType,
  windowValue,
  addToUsetUnitPriceAttributes,
  has_file,
  showLongDescriptions,
  d,
  v,
  va,
  VALUE_NOT_AVAILABLE,
  VALUE_NEW,
  is_not_new_value,
  is_not_new_value_va,
  vap,
  onAttributesLoaded,
  dataSourcesEnabled,
  itemIdBeingEdited
}) => {
  const keyFor = (config) => hash(config);

  let formField;

  switch(config.form_type) {
    case 'UserUnitPriceAttribute':
      formField = <UserUnitPriceAttributeField
          key={keyFor(config)}
          config={config}
          libraryTabConfig={libraryTabConfig}
          options={options}
          setValue={setValue}
          readOnlyValues={readOnlyValues}
          setFetchInProgress={setFetchInProgress}
          setChildSubFormOpen={setChildSubFormOpen}
          value={values[config.name]}
          values={values}
          disabled={disabled}
          register={register}
          errors={errors}
          context={context}
          item={item}
          navSection={navSection}
          requestPublishItemOnChange={requestPublishItemOnChange}
          onAttributesLoaded={onAttributesLoaded}
        />
      break;

    case 'UserUnitPrice':
      addToUsetUnitPriceAttributes(config.name);

      formField = <UserUnitPrice
          key={keyFor(config)}
          config={config}
          value={values[config.name]}
          values={values}
          setValue={setValue}
          windowValue={windowValue}
          options={options}
          item={item}
          navSection={navSection}
          setFetchInProgress={setFetchInProgress}
          disabled={disabled}
          requestGetExtraItems={requestGetExtraItems}
          requestPublishItemOnChange={requestPublishItemOnChange}
          navSectionColumnType={navSectionColumnType}
        />
      break;

    case 'select':
      formField = <SelectFormField
          key={keyFor(config)}
          config={config}
          options={options}
          setValue={setValue}
          item={item}
          navSection={navSection}
          readOnlyValues={readOnlyValues}
          setFetchInProgress={setFetchInProgress}
          requestGetExtraItems={requestGetExtraItems}
          value={values[config.name]}
          values={values}
          requestPublishItemOnChange={requestPublishItemOnChange}
          d={d}
          disabled={disabled || readOnly}
          register={register}
          errors={errors}
          context={context}
        />
      break;

    case 'layout_dimension_select':
      formField = <LayoutDimensionSelectFormField
          key={keyFor(config)}
          config={config}
          value={values[config.name]}
          values={values}
          setValue={setValue}
          register={register}
          errors={errors}
          disabled={disabled || readOnly}
        />
      break;

    case 'hidden':
      formField = <HiddenFormField
          key={keyFor(config)}
          config={config}
          options={options}
          setValue={setValue}
          item={item}
          navSection={navSection}
          setFetchInProgress={setFetchInProgress}
          requestGetExtraItems={requestGetExtraItems}
          value={values[config.name]}
          disabled={disabled}
        />
      break;

    case 'length':
      formField = <LengthFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          register={register}
          errors={errors}
          unit= {config.length_unit != undefined ? config.length_unit : values[config.length_unit_in_values]}
          disabled={disabled}
          readOnly={readOnly}
          readOnlyValues={readOnlyValues}
          context={context}
        />
      break;

    case 'number':
      formField = <NumberFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          values={values}
          register={register}
          errors={errors}
          disabled={disabled}
          readOnly={readOnly}
          options={options}
        />
      break;

    case 'delayed_number':
      formField = <DelayedNumberFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          item={item}
          navSection={navSection}
          requestPublishItemOnChange={requestPublishItemOnChange}
          value={values[config.name]}
          register={register}
          errors={errors}
          disabled={disabled}
          readOnly={readOnly}
        />
      break;

    case 'integer':
      formField = <NumberFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          integerOnly={true}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          values={values}
          register={register}
          errors={errors}
          disabled={disabled}
          readOnly={readOnly}
          options={options}
        />
      break;

    case 'delayed_integer':
      formField = <DelayedNumberFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          item={item}
          navSection={navSection}
          integerOnly={true}
          requestPublishItemOnChange={requestPublishItemOnChange}
          value={values[config.name]}
          register={register}
          errors={errors}
          disabled={disabled}
          readOnly={readOnly}
        />
      break;

    case 'delayed_integer_with_unit':
      formField = <DelayedNumberWithUnitFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          item={item}
          navSection={navSection}
          integerOnly={true}
          setFetchInProgress={setFetchInProgress}
          unit= {config.length_unit != undefined ? config.length_unit : values[config.length_unit_in_values]}
          requestPublishItemOnChange={requestPublishItemOnChange}
          value={values[config.name]}
          register={register}
          errors={errors}
          disabled={disabled}
          readOnly={readOnly}
        />
      break;

    case 'delayed_integer_with_static_units':
      formField = <DelayedNumberWithStaticUnitsFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          integerOnly={true}
          requestPublishItemOnChange={requestPublishItemOnChange}
          value={values[config.name]}
          values={values}
          register={register}
          errors={errors}
          disabled={disabled}
          readOnly={readOnly}
          navSection={navSection}
          item={item}
        />
      break;

    case 'string':
      formField = <StringFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          register={register}
          errors={errors}
          disabled={disabled}
          readOnly={readOnly}
        />
      break;

    case 'file':
      has_file.current = config.name;

      formField = <FileFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          register={register}
          errors={errors}
          disabled={disabled}
        />
      break;

    case 'email':
      formField = <StringFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          register={register}
          errors={errors}
          email={true}
          disabled={disabled}
          readOnly={readOnly}
        />
      break;

    case 'textArea':
      formField = <TextAreaFormField
          key={keyFor(config)}
          config={config}
          setValue={setValue}
          setFetchInProgress={setFetchInProgress}
          value={values[config.name]}
          register={register}
          errors={errors}
          disabled={disabled}
          readOnly={readOnly}
        />
      break;

    case 'currency':
      formField = <CurrencyFormField
        key={keyFor(config)}
        config={config}
        setValue={setValue}
        setFetchInProgress={setFetchInProgress}
        value={values[config.name]}
        disabled={disabled}
        readOnly={readOnly}
      />
      break;

    case 'CurrencyExchange':
      formField = <CurrencyExchange
        key={config.name}
        config={config}
        readOnly={readOnly}
      />
      break;

    case 'LayoutPartGroupsWindows':
      formField = <LayoutPartGroupsWindows
        key={config.name}
        config={config}
        values={values}
        setValue={setValue}
        readOnly={readOnly}
      />
      break;

    case 'LayoutPartGroupsWindowsHidden':
      formField = <LayoutPartGroupsWindowsHidden
        key={config.name}
        config={config}
        values={values}
        setValue={setValue}
      />
      break;

    case 'price':
      formField = <PriceFormField
        key={config.name}
        config={config}
        setValue={setValue}
        setFetchInProgress={setFetchInProgress}
        currency={values['currency']}
        register={register}
        errors={errors}
        value={values[config.name]}
        values={values}
        disabled={disabled}
        readOnly={readOnly}
        precision={config.precision}
        dataSourcesEnabled={dataSourcesEnabled}
        userUnitPriceId={itemIdBeingEdited}
      />
      break;

    case 'price_with_precision_4':
      formField = <PriceFormField
        key={config.name}
        config={config}
        setValue={setValue}
        setFetchInProgress={setFetchInProgress}
        currency={values['currency']}
        register={register}
        errors={errors}
        value={values[config.name]}
        values={values}
        disabled={disabled}
        readOnly={readOnly}
        precision="4"
        dataSourcesEnabled={dataSourcesEnabled}
        userUnitPriceId={itemIdBeingEdited}
      />
      break;

    case 'description_long':
      formField = showLongDescriptions && <DescriptionLong
        key={config.name}
        values={values}
        config={config}
        disabled={disabled}
      />
      break;

    case 'checkboxes':
      formField = <CheckboxesFormField
          key={keyFor(config)}
          config={config}
          options={options}
          setValue={setValue}
          item={item}
          navSection={navSection}
          readOnlyValues={readOnlyValues}
          setFetchInProgress={setFetchInProgress}
          requestGetExtraItems={requestGetExtraItems}
          value={values[config.name]}
          values={values}
          requestPublishItemOnChange={requestPublishItemOnChange}
          d={d}
          disabled={disabled || readOnly}
          register={register}
          errors={errors}
          context={context}
        />
      break;

    case 'boolean':
      formField = <BooleanFormField
          key={keyFor(config)}
          config={config}
          options={options}
          setValue={setValue}
          item={item}
          navSection={navSection}
          readOnlyValues={readOnlyValues}
          setFetchInProgress={setFetchInProgress}
          requestGetExtraItems={requestGetExtraItems}
          value={values[config.name]}
          values={values}
          requestPublishItemOnChange={requestPublishItemOnChange}
          d={d}
          disabled={disabled || readOnly}
          register={register}
          errors={errors}
          context={context}
        />
      break;

    case 'drawing_filtering':
      formField = <DrawingFiltering
        key={keyFor(config)}
        config={config}
        setValue={setValue}
        setFetchInProgress={setFetchInProgress}
        value={values[config.name]}
        register={register}
        errors={errors}
        disabled={disabled}
      />
      break;

    case 'read_only_hash':
      formField = <ReadOnlyHashFormField
        key={keyFor(config)}
        config={config}
        value={values[config.name]}
        setValue={setValue}
      />
      break;
    }

    if(config.visible_if != undefined) {
      return eval(config.visible_if) && formField
    }

    return formField;
};

export default FormFieldFactory;